import React, { useState } from "react";
import {
	Video,
	Calendar,
	Clock,
	User,
	Play,
	ChevronDown,
	ChevronUp,
} from "lucide-react";

/**
 * Convert various YouTube / Vimeo share URLs into an embeddable URL.
 * Returns null if the URL isn't recognised.
 */
const getEmbedUrl = (url) => {
	if (!url) return null;

	// YouTube variants
	let match = url.match(
		/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([a-zA-Z0-9_-]{11})/,
	);
	if (match) return `https://www.youtube.com/embed/${match[1]}`;

	// Vimeo variants
	match = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
	if (match) return `https://player.vimeo.com/video/${match[1]}`;

	return null;
};

const formatDate = (dateStr) => {
	if (!dateStr) return null;
	return new Date(dateStr).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
};

const UserWorkshops = ({
	workshops,
	loading,
	category = "workshop",
	label = "Workshop",
	labelPlural = "Workshops",
}) => {
	const [expandedWorkshop, setExpandedWorkshop] = useState(null);

	// Legacy docs without a category are treated as "workshop".
	const published = workshops.filter(
		(w) => w.published && (w.category || "workshop") === category,
	);

	const toggleExpanded = (id) => {
		setExpandedWorkshop((prev) => (prev === id ? null : id));
	};

	if (loading) {
		return (
			<div className="text-center py-4">
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			</div>
		);
	}

	return (
		<div style={{ maxWidth: "100%", overflow: "hidden" }}>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h5 className="mb-0">
					<Video className="me-2" size={20} />
					{labelPlural}
				</h5>
				{published.length > 0 && (
					<span
						className="badge"
						style={{
							backgroundColor: "rgba(209,90,54,0.2)",
							color: "#d15a36",
							fontSize: "0.8rem",
						}}
					>
						{published.length} available
					</span>
				)}
			</div>

			{published.length === 0 ? (
				<div className="text-center py-5">
					<Video size={48} style={{ opacity: 0.3 }} className="mb-3" />
					<p className="text-muted">
						No {labelPlural.toLowerCase()} available yet.
					</p>
					<p className="text-muted">
						Check back soon — your trainer will post {label.toLowerCase()}{" "}
						recordings here.
					</p>
				</div>
			) : (
				<div>
					{published.map((workshop) => {
						const isExpanded = expandedWorkshop === workshop.id;
						const embedUrl = getEmbedUrl(workshop.videoUrl);

						return (
							<div
								key={workshop.id}
								className="mb-3"
								style={{
									borderRadius: "8px",
									overflow: "hidden",
									border: isExpanded ? "1px solid #d15a36" : "1px solid #333",
									backgroundColor: "#1a1a1a",
									transition: "border-color 0.2s ease",
								}}
							>
								{/* Workshop Header — clickable */}
								<div
									className="p-3"
									onClick={() => toggleExpanded(workshop.id)}
									style={{
										cursor: "pointer",
										borderBottom: isExpanded ? "2px solid #d15a36" : "none",
									}}
								>
									{/* Title row */}
									<div className="d-flex justify-content-between align-items-start gap-2 mb-2">
										<div style={{ flex: 1, minWidth: 0 }}>
											<h6
												className="mb-0"
												style={{
													color: "#fff",
													wordBreak: "break-word",
													display: "flex",
													alignItems: "flex-start",
													gap: "0.5rem",
												}}
											>
												<Video
													size={18}
													style={{
														color: "#d15a36",
														flexShrink: 0,
														marginTop: "2px",
													}}
												/>
												<span>{workshop.title}</span>
											</h6>
										</div>

										{/* Watch button + chevron */}
										<div
											className="d-flex align-items-center gap-2"
											style={{ flexShrink: 0 }}
										>
											<button
												className="btn btn-sm d-flex align-items-center gap-1"
												onClick={(e) => {
													e.stopPropagation();
													toggleExpanded(workshop.id);
												}}
												style={{
													backgroundColor: isExpanded
														? "#d15a36"
														: "rgba(209,90,54,0.2)",
													color: isExpanded ? "#fff" : "#d15a36",
													border: "none",
													padding: "0.45rem 1rem",
													borderRadius: "6px",
													fontWeight: 600,
													fontSize: "0.9rem",
													whiteSpace: "nowrap",
												}}
											>
												<Play size={16} />
												Watch
											</button>
											{isExpanded ? (
												<ChevronUp size={20} style={{ color: "#d15a36" }} />
											) : (
												<ChevronDown
													size={20}
													style={{
														color: "rgba(255,255,255,0.5)",
													}}
												/>
											)}
										</div>
									</div>

									{/* Description — always visible, 2-line clamp */}
									{workshop.description && (
										<p
											className="mb-2"
											style={{
												color: "rgba(255,255,255,0.55)",
												fontSize: "0.85rem",
												lineHeight: 1.45,
												display: "-webkit-box",
												WebkitLineClamp: 2,
												WebkitBoxOrient: "vertical",
												overflow: "hidden",
												margin: 0,
												marginBottom: "0.5rem",
												wordBreak: "break-word",
											}}
										>
											{workshop.description}
										</p>
									)}

									{/* Meta badges */}
									<div className="d-flex flex-wrap align-items-center gap-2">
										{workshop.instructor && (
											<span
												className="badge"
												style={{
													backgroundColor: "#d15a36",
													fontSize: "0.75rem",
												}}
											>
												<User
													size={10}
													className="me-1"
													style={{ marginTop: "-1px" }}
												/>
												{workshop.instructor}
											</span>
										)}
										{workshop.workshopDate && (
											<small
												style={{
													color: "rgba(255,255,255,0.45)",
												}}
											>
												<Calendar size={12} className="me-1" />
												{formatDate(workshop.workshopDate)}
											</small>
										)}
										{workshop.duration && (
											<small
												style={{
													color: "rgba(255,255,255,0.45)",
												}}
											>
												<Clock size={12} className="me-1" />
												{workshop.duration}
											</small>
										)}
									</div>
								</div>

								{/* Expanded: video player */}
								{isExpanded && (
									<div className="p-2 p-md-3">
										{embedUrl ? (
											<div className="workshop-video-wrapper">
												<iframe
													src={embedUrl}
													title={workshop.title}
													allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
													allowFullScreen
													className="workshop-video-iframe"
												/>
											</div>
										) : (
											<div
												className="text-center py-4"
												style={{
													backgroundColor: "#2a2a2a",
													borderRadius: "8px",
													border: "1px solid #333",
												}}
											>
												<p
													className="mb-2"
													style={{
														color: "rgba(255,255,255,0.5)",
													}}
												>
													This video can't be embedded directly.
												</p>
												<a
													href={workshop.videoUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="btn btn-sm"
													style={{
														backgroundColor: "#d15a36",
														color: "#fff",
														border: "none",
													}}
												>
													Open Video in New Tab
												</a>
											</div>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default UserWorkshops;
